import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { Box, Anchor, IconChevronDownOutline } from '@zillow/constellation';
import { yieldCallback } from '@zillow/yield-callback';
import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from 'styled-components';
import { getCmsInfoForPage } from '../../utils/clickstream/getCmsInfoForPage.js';
import { usePageProps } from '../PagePropsProvider/PagePropsProvider.js';
import { sendEvent } from './B2BMainNav.utils.js';
import Dropdown from './Dropdown.js';

/**
 * A function that handles mouse enter and mouse leave events to show/hide dropdown menu, and closes the dropdown when clicking outside of it.
 *
 * @param {Object} item - The item object containing information about the menu item
 * @param {Object} sx - The style object
 * @return {JSX.Element} The JSX element representing the menu item with dropdown if it has children, otherwise just the menu item
 */
var MenuItem = function MenuItem(_ref) {
  var item = _ref.item,
    transparent = _ref.transparent,
    sx = _ref.sx;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    dropdown = _useState2[0],
    setDropdown = _useState2[1];
  var ref = useRef();
  var theme = useTheme();
  var onMouseEnter = function onMouseEnter() {
    if (window.innerWidth > theme.constellation.breakpoints.xl) {
      setDropdown(true);
    }
  };
  var onMouseLeave = function onMouseLeave() {
    if (window.innerWidth > theme.constellation.breakpoints.xl) {
      setDropdown(false);
    }
  };

  // Closes the dropdown when clicking outside of it
  useEffect(function () {
    if (window.innerWidth <= theme.constellation.breakpoints.xl) {
      return;
    }
    var handler = function handler(event) {
      if (dropdown && ref.current && !ref.current.contains(event.target)) {
        setDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return function () {
      // Cleanup the event listener
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [dropdown, theme.constellation.breakpoints.xl]);
  var pageProps = usePageProps();
  var cmsInfo = getCmsInfoForPage(pageProps);

  /**
   * Handles the click event on the item, toggles the dropdown, and sends a 'cms_anchor' event.
   *
   * @param {Object} item - The item object clicked
   * @return {void} No return value
   */
  var handleItemClick = function handleItemClick(item) {
    sendEvent('cms_anchor', item.label, item.url, cmsInfo);
    window.open(item.url, '_self');
  };

  // If the item has children, render it as a dropdown
  if (item.children && item.children.length > 0) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
      as: "li",
      sx: Object.assign({}, sx, {
        color: transparent ? '$colors.textWhite' : '$colors.textDark',
        fontFamily: '$fonts.sansSerif',
        lineHeight: '$lineHeights.body',
        alignItems: 'center',
        position: 'relative',
        '@media $xl': {
          paddingRight: '$spacing.sm'
        },
        '@media $lg_lte': {
          borderBottom: 'solid 1px $colors.borderLight',
          flexDirection: 'column',
          width: '100%'
        }
      }),
      ref: ref,
      onMouseEnter: onMouseEnter,
      onMouseLeave: onMouseLeave
    }, /*#__PURE__*/React.createElement(Box, {
      as: Anchor,
      href: item.url,
      "aria-haspopup": "menu",
      "aria-expanded": dropdown ? 'true' : 'false',
      onClick: function onClick(e) {
        e.preventDefault();
        handleItemClick(item);
      },
      sx: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        color: transparent ? '$colors.textWhite !important' : '$colors.textDark !important',
        textDecoration: 'none !important',
        padding: '$spacing.xs $spacing.sm',
        paddingRight: '$spacing.none',
        ':visited': {
          color: transparent ? '$colors.textWhite !important' : '$colors.textDark !important'
        },
        ':hover': {
          color: transparent ? '$colors.textWhite !important' : '$colors.brand !important',
          cursor: 'pointer'
        },
        '@media $lg_lte': {
          padding: '$spacing.sm'
        }
      }
    }, item.label), /*#__PURE__*/React.createElement(Box, {
      sx: {
        display: 'flex',
        padding: '$spacing.none',
        alignItems: 'center',
        '@media $lg_lte': {
          position: 'absolute',
          right: 0,
          top: 0,
          padding: 'calc($spacing.xs * 2.5)'
        }
      },
      onClick: function onClick() {
        return setDropdown(!dropdown);
      }
    }, /*#__PURE__*/React.createElement(IconChevronDownOutline, {
      size: "xs",
      marginLeft: {
        lg_gte: 'xs'
      }
    })), /*#__PURE__*/React.createElement(Dropdown, {
      submenus: item.children,
      dropdown: dropdown
    })));
  }
  // If the item has no children
  return /*#__PURE__*/React.createElement(Box, {
    as: "li",
    sx: Object.assign({}, sx, {
      color: transparent ? '$colors.textWhite' : '$colors.textDark',
      fontFamily: '$fonts.sansSerif',
      lineHeight: '$lineHeights.body',
      alignItems: 'center',
      '@media $lg_lte': {
        borderBottom: 'solid 1px $colors.borderLight',
        flexDirection: 'column',
        width: '100%'
      },
      ':hover, :focus': {
        color: transparent ? '$colors.textWhite' : '$colors.brand !important',
        cursor: 'pointer'
      }
    })
  }, /*#__PURE__*/React.createElement(Box, {
    as: Anchor,
    sx: {
      color: transparent ? '$colors.textWhite !important' : '$colors.textDark !important',
      padding: '$spacing.xs $spacing.sm',
      textDecoration: 'none !important',
      ':visited': {
        color: transparent ? '$colors.textWhite !important' : '$colors.textDark !important'
      },
      ':hover': {
        color: transparent ? '$colors.textWhite' : '$colors.brand !important'
      },
      '@media $lg_lte': {
        padding: '$spacing.sm',
        width: '100%'
      }
    },
    href: item.url,
    onClick: yieldCallback(function () {
      handleItemClick(item);
    })
  }, item.label));
};
var MenuItem$1 = MenuItem;

export { MenuItem$1 as default };
